import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OrganizationDto } from '@api/organization';
import { MetaDto } from '@api/sync';
import { loadContentsSuccess } from '@app/content/_ngrx/content.actions';
import { loadExhibitionsSuccess } from '@app/exhibition/_ngrx/exhibition.actions';
import { loadStationsSuccess } from '@app/station/_ngrx/station.actions';
import { loadToursSuccess } from '@app/tour/_ngrx/tour.actions';
import { appConfig } from '@assets/config/app.config';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { State } from '../ngrx';
import { setDynamicContentUrl } from '../ngrx/app.actions';
import { AppService } from './app.service';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class SyncService {
  private readonly appState$ = inject<Store<State>>(Store);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly httpClient = inject(HttpClient);
  private readonly appService = inject(AppService);


  private readonly syncMetaKey: string = 'syncMeta';
  private readonly baseUrl = appConfig.cms.apiUrl;

  storeSyncMeta(syncMeta: MetaDto) {
    this.localStorageService.write(this.syncMetaKey, syncMeta);
  }

  getSyncMetaInfo(organization: OrganizationDto) {
    // 1. syncV20MetaGet is called in the beginning and stored in local storage
    this.httpClient.get(`${this.baseUrl}/sync/v2.1/be/meta`, {
      headers: {
        'tenantId': organization.tenantId,
        'nc-channel': organization.urlSuffix
      }
    })
    .pipe(
      take(1)
    ).subscribe((meta: MetaDto) => {
      const previousMeta = this.localStorageService.read(this.syncMetaKey) as MetaDto;
        // 2. If version is > -1 Get JSON with syncV20JsonGet should also be stored
        if (!previousMeta?.buildNumber || meta.buildNumber > previousMeta.buildNumber) {
          this.getSyncJSONWithMeta(meta);
          this.storeSyncMeta(meta);
        } else if (meta.buildNumber >= previousMeta.buildNumber) {
          this.getSyncJSONWithMeta(meta);
        } else {
          this.getSyncJSONWithMeta(meta);
          this.storeSyncMeta(meta);
        }
    });
  }

  tryOfflineSync() {
    const previousMeta = this.localStorageService.read(this.syncMetaKey) as MetaDto;
    this.appService.organization$
      .pipe(
        take(1)
      ).subscribe(organization => {
        this.appService.fillOrganizationFields(organization);
      });
    if (previousMeta) {
      this.getSyncJSONWithMeta(previousMeta);
    }
  }

  getDetailJSON(key: string, basePath: string, json: any) {
    if (json[key]?.length > 0) {
      const jsonUrl = json[key][0];
      fetch(basePath + jsonUrl)
        .then(res => res.json())
        .then(detailJson => {
          if (key === 'station') {
            this.appState$.dispatch(loadStationsSuccess({stations: detailJson}));
          } else if (key === 'tour') {
            this.appState$.dispatch(loadToursSuccess({tours: detailJson}));
          } else if (key === 'content') {
            this.appState$.dispatch(loadContentsSuccess({contents: detailJson}));
          } else if (key === 'exhibition') {
            this.appState$.dispatch(loadExhibitionsSuccess({exhibitions: detailJson}));
          }
        });
    }
  }

  setDynamicContentUrl(contentUrl: string) {
    this.appState$.dispatch(setDynamicContentUrl({contentUrl}));
  }

  getSyncJSONWithMeta(meta: MetaDto) {
    this.setDynamicContentUrl(meta.contentBasePath);
    fetch(meta.syncBasePath + meta.jsonFile)
      .then(res => res.json())
      .then((json) => {
        this.getDetailJSON('station', meta.syncBasePath, json);
        this.getDetailJSON('content', meta.syncBasePath, json);
        this.getDetailJSON('tour', meta.syncBasePath, json);
        this.getDetailJSON('exhibition', meta.syncBasePath, json);
      })
      .catch(err => console.log('error', err));
  }
}
